import { Logger } from '@streem/logger';
import { Bookmark, DetailSession, Recording, Streemshot } from '@streem/sdk-core';
import { useMediaBreakpoint, useObservable } from '@streem/sdk-react';
import { RefObject, useRef, useState } from 'react';
import { WallItem } from 'streem-sdk-protobuf';
import { StreemshotImageData } from '../util/media_gallery_selected_utils';
import { useDetailSession } from './detail_session_hooks';
import { useCompanySettingsStore } from './use_global_context';
import {
    TranscriptionContext,
    useInitializeTranscriptionContextProvider,
} from './use_transcription_context';

export interface UseMediaGalleryReturn {
    detailSession: DetailSession;
    streemshots: Streemshot[];
    recordings: Recording[];
    bookmarks: Bookmark[];
    selectedRecordingIds: Set<string>;
    selectedStreemshotIds: Set<string>;
    streemshotsImageDataRef: RefObject<StreemshotImageData>;
    selectionSize: number;
    isMobile: boolean;
    selectionVisible: boolean;
    handleArtifactSelect: (artifactType: WallItem.ArtifactType, artifactSid: string) => void;
    clearSelection: () => void;
    errorMessage: string;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
    setSelectedStreemshotIds: React.Dispatch<React.SetStateAction<[Set<string>]>>;
    transcriptionUiEnabled: boolean;
    initialTranscriptionContext: TranscriptionContext;
}

export function useMediaGallery(log: Logger): UseMediaGalleryReturn {
    const detailSession = useDetailSession();
    const { transcriptionUiEnabled = false } = useCompanySettingsStore();

    const { isMobile } = useMediaBreakpoint();

    const [streemshots = []] = useObservable(detailSession.streemshots.responses);
    const [recordings = []] = useObservable(detailSession.recording.recordings);
    const [bookmarks = []] = useObservable(detailSession.bookmarks.bookmarks);

    const [[selectedStreemshotIds], setSelectedStreemshotIds] = useState<[Set<string>]>([
        new Set(),
    ]);

    const [errorMessage, setErrorMessage] = useState('');
    const [[selectedRecordingIds], setSelectedRecordingIds] = useState<[Set<string>]>([new Set()]);

    const selectionSize = selectedRecordingIds.size + selectedStreemshotIds.size;
    const selectionVisible = selectionSize > 0 || isMobile;

    const streemshotsImageDataRef = useRef<StreemshotImageData>({});
    const handleArtifactSelect = (artifactType: WallItem.ArtifactType, artifactSid: string) => {
        setErrorMessage('');
        const toggleInclusion = (set: Set<string>) => {
            const included = set.delete(artifactSid);
            if (!included) {
                set.add(artifactSid);
            }

            return set;
        };

        switch (artifactType) {
            case WallItem.ArtifactType.RECORDING:
                setSelectedRecordingIds([toggleInclusion(selectedRecordingIds)]);
                break;
            case WallItem.ArtifactType.STREEMSHOT:
                setSelectedStreemshotIds([toggleInclusion(selectedStreemshotIds)]);
                break;
            default:
                log.error('Attempted to select artifact of unsupported type ' + artifactType);
                return;
        }
    };

    const clearSelection = () => {
        setSelectedRecordingIds([new Set()]);
        setSelectedStreemshotIds([new Set()]);
    };
    const initialTranscriptionContext = useInitializeTranscriptionContextProvider();

    return {
        detailSession,
        streemshots,
        selectedStreemshotIds,
        recordings,
        selectedRecordingIds,
        streemshotsImageDataRef,
        selectionSize,
        isMobile,
        selectionVisible,
        handleArtifactSelect,
        clearSelection,
        errorMessage,
        bookmarks,
        setErrorMessage,
        setSelectedStreemshotIds,
        transcriptionUiEnabled,
        initialTranscriptionContext: initialTranscriptionContext,
    };
}
